import { useState } from 'react'
import { ArrowLeft, Settings, Loader2, Play, Save } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useToast } from '@/hooks/use-toast'
import { usePlaywrightScriptWorkflow } from '@/hooks/use-playwright-script-workflow'
import { useAuth } from '@/hooks/useAuth'
import { LiveBrowserViewer } from '@/components/anchor-browser/LiveBrowserViewer'
import { useNavigate } from 'react-router-dom'
import { api } from '@/lib/api'


export function PlaywrightScripts() {
  const navigate = useNavigate()
  const { toast } = useToast()
  useAuth() // Authentication hook

  const [showSaveDialog, setShowSaveDialog] = useState(false)
  const [scriptName, setScriptName] = useState('')
  const [scriptDescription, setScriptDescription] = useState('')
  const [generatePrompt, setGeneratePrompt] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)
  // Removed activeTab - no longer using tabs
  const [directCode, setDirectCode] = useState(`console.info("Launching browser...");

/* To make things easier, we've setup Playwright using the window variables.
 You can access it and your API key using window.playwright or window.connectionString. */
console.info('Connected!');

// For demo purposes, we'll wait a second so we can watch.
await new Promise((resolve) => setTimeout(resolve, 1000));

// Navigate to Hacker News
await page.goto("https://news.ycombinator.com");

console.info("Success!");`)
  const [isExecutingDirect, setIsExecutingDirect] = useState(false)

  const {
    currentSession,
    executeCodeViaCDP,
    saveScript
  } = usePlaywrightScriptWorkflow()

  // Removed MCP auto-connect

  const handleGenerateScript = async () => {
    if (!generatePrompt.trim()) {
      toast({
        title: 'Prompt required',
        description: 'Please describe what you want the script to do',
        variant: 'destructive'
      })
      return
    }

    setIsGenerating(true)
    try {
      // Call backend to generate and save a Stagehand-compatible script
      const res: any = await api.request('/playwright-scripts/generate', {
        method: 'POST',
        body: JSON.stringify({
          prompt: generatePrompt,
        })
      })

      const generatedCode = res?.script?.code || ''
      if (!generatedCode) {
        throw new Error('No code returned from generator')
      }

      console.log('Generated script (server):', generatedCode.substring(0, 200) + '...')
      setDirectCode(generatedCode)
      setGeneratePrompt('')

      toast({
        title: 'Script Generated',
        description: `Generated ${generatedCode.split('\n').length} lines of code. Check the editor below.`,
      })
    } catch (error) {
      console.error('Failed to generate script:', error)
      toast({
        title: 'Generation Failed',
        description: 'Failed to generate script. Please try again.',
        variant: 'destructive'
      })
    } finally {
      setIsGenerating(false)
    }
  }


  const handleSaveScript = async () => {
    if (!directCode || !scriptName.trim()) {
      toast({
        title: "Missing Information",
        description: "Please provide a script name",
        variant: "destructive"
      })
      return
    }

    try {
      await saveScript(scriptName, scriptDescription)
      setShowSaveDialog(false)
      setScriptName('')
      setScriptDescription('')
      toast({
        title: "Script Saved",
        description: `Successfully saved "${scriptName}"`,
      })
    } catch (error) {
      console.error('Failed to save script:', error)
      toast({
        title: "Error",
        description: "Failed to save script",
        variant: "destructive"
      })
    }
  }

  // Removed handleGenerateScript function

  const handleExecuteDirectCode = async () => {
    if (!directCode.trim()) {
      toast({
        title: "Error",
        description: "Please enter some code to execute",
        variant: "destructive"
      })
      return
    }

    setIsExecutingDirect(true)
    try {
      console.log('Current session before execution:', currentSession)
      // If no session exists, executeCodeViaCDP will create one automatically
      await executeCodeViaCDP(directCode, currentSession?.sessionId || undefined)
      console.log('Current session after execution:', currentSession)
      toast({
        title: "Code Executed",
        description: "Your Playwright script has been executed successfully. Check the live browser view.",
      })
    } catch (error) {
      console.error('Failed to execute code:', error)
      toast({
        title: "Error",
        description: "Failed to execute code. Please check your script and try again.",
        variant: "destructive"
      })
    } finally {
      setIsExecutingDirect(false)
    }
  }

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <div className="border-b p-4 flex items-center justify-between min-h-[80px]">
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-bold truncate">
              Authoring
            </h1>
            <p className="text-muted-foreground text-sm truncate">
              Write and execute browser automation scripts with live preview
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm">
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
        </div>
      </div>

      {/* Main Content - Full Width */}
      <div className="flex-1 flex flex-col lg:flex-row">
        {/* Code Editor - Responsive Width */}
        <div className="w-full lg:w-1/2 xl:w-2/5 p-4 lg:border-r">
          <div className="h-full flex flex-col gap-4">
            {/* Text Generation Section */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Generate Script</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Describe what you want to automate and we'll generate a Stagehand script
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                <Textarea
                  value={generatePrompt}
                  onChange={(e) => setGeneratePrompt(e.target.value)}
                  placeholder="e.g., Go to Google and search for 'Playwright testing'"
                  className="resize-none"
                  rows={2}
                />
                <Button
                  onClick={handleGenerateScript}
                  disabled={isGenerating || !generatePrompt.trim()}
                  size="sm"
                  className="w-full"
                >
                  {isGenerating ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <span className="mr-2">✨</span>
                  )}
                  {isGenerating ? 'Generating...' : 'Generate Script'}
                </Button>
              </CardContent>
            </Card>

            {/* Code Editor */}
            <Card className="flex-1 flex flex-col">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Code Editor</CardTitle>
                  </div>
                  <Button
                    onClick={handleExecuteDirectCode}
                    disabled={isExecutingDirect}
                    size="sm"
                  >
                    {isExecutingDirect ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="h-4 w-4 mr-2" />
                    )}
                    Execute
                  </Button>
                </div>
              </CardHeader>
            <CardContent className="flex-1 flex flex-col">
              <Textarea
                value={directCode}
                onChange={(e) => setDirectCode(e.target.value)}
                placeholder="Enter your Playwright/Stagehand script here..."
                className="flex-1 font-mono text-sm resize-none"
                style={{ minHeight: '400px' }}
              />
              <p className="text-sm text-muted-foreground mt-2">
                Click Execute to run your script. A browser session will be created automatically if needed.
              </p>
            </CardContent>
            </Card>
          </div>
        </div>

        {/* Live Browser Session - Redesigned for prominence */}
        <div className="flex-1 p-4 min-h-0">
          <div className="h-full flex flex-col bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border-2 border-slate-200 shadow-lg">
            <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-white/80 backdrop-blur-sm rounded-t-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <Play className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Live Browser Session</h2>
                  <p className="text-sm text-slate-600">Real-time view of script execution</p>
                </div>
              </div>
              {currentSession.sessionId && (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium text-slate-700">Active</span>
                </div>
              )}
            </div>

            <div className="flex-1 p-4 min-h-0">
              {(() => {
                console.log('Live browser viewer - currentSession:', currentSession)
                console.log('Live browser viewer - liveUrl:', currentSession?.liveUrl)
                return currentSession?.liveUrl ? (
                  <div className="h-full bg-white rounded-lg shadow-inner border border-slate-200 overflow-hidden">
                    <LiveBrowserViewer
                      liveUrl={currentSession.liveUrl}
                      taskId={currentSession.sessionId!}
                      taskStatus={currentSession.status}
                      className="h-full w-full"
                    />
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center bg-white rounded-lg shadow-inner border border-slate-200">
                    <div className="text-center">
                      <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl flex items-center justify-center">
                        <div className="w-10 h-10 bg-slate-300 rounded-lg opacity-50" />
                      </div>
                      <h3 className="text-lg font-medium text-slate-900 mb-2">Ready for Execution</h3>
                      <p className="text-slate-600 mb-1">Click Execute to start your browser session</p>
                      <p className="text-sm text-slate-500">Live browser view will appear here</p>
                    </div>
                  </div>
                )
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Save Script Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Save Playwright Script</DialogTitle>
            <DialogDescription>
              Save the generated script to your library for future use
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="script-name">Script Name</Label>
              <Input
                id="script-name"
                value={scriptName}
                onChange={(e) => setScriptName(e.target.value)}
                placeholder="e.g., Google Search Automation"
              />
            </div>

            <div>
              <Label htmlFor="script-description">Description (Optional)</Label>
              <Input
                id="script-description"
                value={scriptDescription}
                onChange={(e) => setScriptDescription(e.target.value)}
                placeholder="Brief description of what this script does"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveScript} disabled={!scriptName.trim()}>
              <Save className="h-4 w-4 mr-2" />
              Save Script
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Removed Generate Script Dialog */}
    </div>
  )
}
